import * as vp from 'vopay';
import { createContext, JSX, ReactNode, useContext, useState } from 'react';

export type PermissionsCtx = {
  permissions: vp.ApiClientPermissions;
  setPermissions: (permissions: { [key: string]: keyof vp.ApiClientPermissions } | undefined) => void;
  reset: () => void;
};

export const PermissionsContext = createContext<PermissionsCtx>({
  permissions: {
    ViewSubaccounts: false,
    AddSubaccounts: false,
    CreatePayments: false,
    CreateCollections: false,
    ViewBeneficiaries: false,
    AddBeneficiaries: false,
    ViewBulkFileUpload: false,
    CreateBulkFileUpload: false,
    ViewScheduledTransactions: false,
    ViewTransactionHistory: false,
    ViewSettings: false,
    AddSettings: false,
    ViewUsers: false,
    AddUsers: false,
    ApproveTransactions: false,
    ManageUserPermissions: false,
    CreateAccountTransfer: false,
    ViewClientAccounts: false,
    AddClientAccounts: false,
    CreateClientAccountTransfers: false,
    CreateConversions: false,
    ManageDefaultPayments: false,
    CreateSupportTickets: false,
    ViewSupportTickets: false,
    ViewReconciliation: false,
    ViewAccountingBillsAndInvoices: false,
    ProcessAccountingBillsAndInvoices: false,
    TransactionCancellation: false,
    ViewTransactionApprovalWorkflow: false,
    CreateTransactionApprovalWorkflow: false,
    ViewTransactionReport: false,
    CreateTransactionReport: false,
    ManageTransactionGroups: false,
    ManageEmailDomains: false,
    ManageInterestAccounts: false,
    ViewInterestAccounts: false,
    ViewElinxSettings: false,
    ManageElinxSettings: false,
    CreateBills: false,
    CreateInvoices: false,
    ViewBankAccountTransactionHistory: false,
    ManageDisputes: false,
    ManageBlockedAccounts: false,
  },
  setPermissions: () => {},
  reset: () => {},
});

export const PermissionsProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [storedPermissions, setStoredPermissions] = useState<
    { [key: string]: keyof vp.ApiClientPermissions } | undefined
  >();

  const mapPermissions = (userPermissionsList: vp.PermissionsApiResponse | undefined) => {
    const userPermissionsDefaults: vp.ApiClientPermissions = {
      ViewSubaccounts: false,
      AddSubaccounts: false,
      CreatePayments: false,
      CreateCollections: false,
      ViewBeneficiaries: false,
      AddBeneficiaries: false,
      ViewBulkFileUpload: false,
      CreateBulkFileUpload: false,
      ViewScheduledTransactions: false,
      ViewTransactionHistory: false,
      ViewSettings: false,
      AddSettings: false,
      ViewUsers: false,
      AddUsers: false,
      ManageUserPermissions: false,
      CreateAccountTransfer: false,
      ViewClientAccounts: false,
      AddClientAccounts: false,
      CreateClientAccountTransfers: false,
      CreateConversions: false,
      ManageDefaultPayments: false,
      CreateSupportTickets: false,
      ViewSupportTickets: false,
      ViewReconciliation: false,
      ViewAccountingBillsAndInvoices: false,
      ProcessAccountingBillsAndInvoices: false,
      TransactionCancellation: false,
      ApproveTransactions: false,
      ViewTransactionApprovalWorkflow: false,
      CreateTransactionApprovalWorkflow: false,
      ViewTransactionReport: false,
      CreateTransactionReport: false,
      ManageTransactionGroups: false,
      ManageEmailDomains: false,
      ManageInterestAccounts: false,
      ViewInterestAccounts: false,
      ViewElinxSettings: false,
      ManageElinxSettings: false,
      CreateBills: false,
      CreateInvoices: false,
      ManageDisputes: false,
      ManageBlockedAccounts: false,
    };

    if (!userPermissionsList) return userPermissionsDefaults;

    return Object.values(userPermissionsList).reduce(
      (acc, perm) => {
        acc[perm] = true;

        return acc;
      },
      { ...userPermissionsDefaults },
    );
  };

  return (
    <PermissionsContext.Provider
      value={{
        permissions: mapPermissions(storedPermissions),
        setPermissions: setStoredPermissions,
        reset: () => setStoredPermissions(undefined),
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsCtx => useContext(PermissionsContext);
